import React, { useState, useEffect } from "react";
import { UserPlus, Upload, Mail } from "lucide-react";
import toast from "react-hot-toast";
import FighterAccountForm from "../components/fighter-account-form";
import BulkUploadFighter from "../components/bulk-upload-fighter";
import AccountResultsTable from "../components/account-results-table";
import { createFighterAccount } from "../utils/firebase";
import { Timestamp } from "firebase/firestore";

export default function CreateFighterAccount() {
    const [activeTab, setActiveTab] = useState("single"); // "single" or "bulk"
    const [sendEmail, setSendEmail] = useState(false);
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState([]);
    const [bulkFighters, setBulkFighters] = useState([]);
    const [formErrors, setFormErrors] = useState({});

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phoneNumber: "+60",
        nationality: "Malaysian",
        birthdate: null,
        age: "",
        gender: "",
        weightClass: "",
        height: "",
        icOrPassport: "",
        gymNameOrTeamName: "",
        walkOutMusic: "",
        igUsername: "",
        emergencyContactNumber: "+60",
        fighterPicture: null,
        records: {
            BOXING: { win: 0, loss: 0, draw: 0 },
            MUAY_THAI: { win: 0, loss: 0, draw: 0 },
            KICKBOXING: { win: 0, loss: 0, draw: 0 },
            MMA: { win: 0, loss: 0, draw: 0 },
        },
    });

    const validateForm = () => {
        const errors = {};

        if (!formData.name || formData.name.trim() === "") {
            errors.name = "Name is required";
        }

        if (!formData.email || formData.email.trim() === "") {
            errors.email = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = "Invalid email format";
        }

        if (!formData.phoneNumber || formData.phoneNumber.trim() === "") {
            errors.phoneNumber = "Phone number is required";
        } else if (
            !/^\+[1-9]\d{9,14}$/.test(formData.phoneNumber.trim())
        ) {
            errors.phoneNumber =
                "Invalid phone format (use E.164: +60123456789)";
        }

        if (formData.gender && !["M", "F"].includes(formData.gender)) {
            errors.gender = "Gender must be M or F";
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleBulkParseSuccess = (fighters) => {
        setBulkFighters(fighters);
    };

    const [bulkUploadedImages, setBulkUploadedImages] = useState({});

    const handleBulkImagesChange = (images) => {
        setBulkUploadedImages(images);
    };

    // Helper function to convert File to base64
    const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                const base64 = reader.result.split(",")[1];
                // Get file extension
                const fileName = file.name;
                const fileExt = fileName.split(".").pop().toLowerCase();
                resolve({ base64, extension: fileExt });
            };
            reader.onerror = reject;
        });
    };

    // Generate password preview (same logic as cloud function)
    const generatePasswordPreview = (name, phoneNumber) => {
        if (!name || !phoneNumber) return "";
        const firstName = name.split(" ")[0];
        const phoneLast4 = phoneNumber.replace(/[^0-9]/g, "").slice(-4);
        return `${firstName}${phoneLast4}!`;
    };

    const passwordPreview = generatePasswordPreview(
        formData.name,
        formData.phoneNumber
    );

    const handleSingleSubmit = async () => {
        if (!validateForm()) {
            toast.error("Please fix validation errors");
            return;
        }

        setLoading(true);
        setResults([]);

        try {
            let fighterPictureBase64 = null;
            let fighterPictureExtension = null;

            // Convert image to base64 if provided
            if (formData.fighterPicture instanceof File) {
                const imageData = await fileToBase64(formData.fighterPicture);
                fighterPictureBase64 = imageData.base64;
                fighterPictureExtension = imageData.extension;
            }

            // Exclude fighterPicture from formData since we're sending base64 instead
            const { fighterPicture, ...formDataWithoutPicture } = formData;
            const fighterData = {
                ...formDataWithoutPicture,
                email: formData.email.trim().toLowerCase(),
                phoneNumber: formData.phoneNumber.trim(),
                fighterPictureBase64: fighterPictureBase64,
                fighterPictureExtension: fighterPictureExtension,
            };

            const result = await createFighterAccount({
                fighters: [fighterData],
                sendEmail: sendEmail,
            });

            if (result.data?.error) {
                toast.error(result.data.error);
                return;
            }

            if (result.data?.results) {
                setResults(result.data.results);
                const successCount = result.data.results.filter(
                    (r) => r.success
                ).length;
                if (successCount > 0) {
                    toast.success(`Successfully created ${successCount} account(s)`);
                    // Reset form
                    setFormData({
                        name: "",
                        email: "",
                        phoneNumber: "",
                        nationality: "Malaysian",
                        birthdate: null,
                        age: "",
                        gender: "",
                        weightClass: "",
                        height: "",
                        icOrPassport: "",
                        gymNameOrTeamName: "",
                        walkOutMusic: "",
                        igUsername: "",
                        emergencyContactNumber: "",
                        fighterPicture: null,
                        records: {
                            BOXING: { win: 0, loss: 0, draw: 0 },
                            MUAY_THAI: { win: 0, loss: 0, draw: 0 },
                            KICKBOXING: { win: 0, loss: 0, draw: 0 },
                            MMA: { win: 0, loss: 0, draw: 0 },
                        },
                    });
                    setFormErrors({});
                }
            }
        } catch (error) {
            console.error("Error creating fighter account:", error);
            toast.error("Failed to create account: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    // Reset bulk uploaded images when CSV is cleared
    useEffect(() => {
        if (bulkFighters.length === 0) {
            setBulkUploadedImages({});
        }
    }, [bulkFighters]);

    const handleBulkSubmit = async () => {
        if (!bulkFighters || bulkFighters.length === 0) {
            toast.error("Please upload and parse a CSV file first");
            return;
        }

        // Validate all images are uploaded
        const allImagesUploaded = bulkFighters.every((_, index) => bulkUploadedImages[index]);
        if (!allImagesUploaded) {
            toast.error("Please upload profile pictures for all fighters before submitting");
            return;
        }

        setLoading(true);
        setResults([]);

        try {
            // Convert all images to base64
            const fightersWithImages = await Promise.all(
                bulkFighters.map(async (fighter, index) => {
                    const imageFile = bulkUploadedImages[index];
                    let fighterPictureBase64 = null;
                    let fighterPictureExtension = null;

                    if (imageFile instanceof File) {
                        const imageData = await fileToBase64(imageFile);
                        fighterPictureBase64 = imageData.base64;
                        fighterPictureExtension = imageData.extension;
                    }

                    // Exclude fighterPicture from fighter data since we're sending base64 instead
                    const { fighterPicture, ...fighterWithoutPicture } = fighter;
                    return {
                        ...fighterWithoutPicture,
                        fighterPictureBase64: fighterPictureBase64,
                        fighterPictureExtension: fighterPictureExtension,
                    };
                })
            );

            const result = await createFighterAccount({
                fighters: fightersWithImages,
                sendEmail: sendEmail,
            });

            if (result.data?.error) {
                toast.error(result.data.error);
                return;
            }

            if (result.data?.results) {
                setResults(result.data.results);
                const successCount = result.data.results.filter(
                    (r) => r.success
                ).length;
                const failureCount = result.data.results.filter(
                    (r) => !r.success
                ).length;

                if (successCount > 0) {
                    toast.success(
                        `Successfully created ${successCount} account(s)${
                            failureCount > 0
                                ? `, ${failureCount} failed`
                                : ""
                        }`
                    );
                } else {
                    toast.error("All accounts failed to create");
                }
            }
        } catch (error) {
            console.error("Error creating fighter accounts:", error);
            toast.error("Failed to create accounts: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page-size flex flex-col gap-4 justify-start p-4">
            {/* Header */}
            <div className=" p-4 bg-base-200">
                <h1 className="text-2xl font-bold text-center">
                    CREATE FIGHTER ACCOUNT
                </h1>
            </div>

            {/* Tab Switcher */}
            <div className="tabs tabs-boxed">
                <button
                    className={`tab flex-1 ${
                        activeTab === "single" ? "tab-active" : ""
                    }`}
                    onClick={() => setActiveTab("single")}
                >
                    <UserPlus size={18} className="mr-2" />
                    Single Account
                </button>
                <button
                    className={`tab flex-1 ${
                        activeTab === "bulk" ? "tab-active" : ""
                    }`}
                    onClick={() => setActiveTab("bulk")}
                >
                    <Upload size={18} className="mr-2" />
                    Bulk Upload
                </button>
            </div>

            {/* Email Notification Toggle */}
            <div className="border-2 border-base-content p-4">
                <label className="label cursor-pointer justify-start gap-4">
                    <input
                        type="checkbox"
                        className="checkbox"
                        checked={sendEmail}
                        onChange={(e) => setSendEmail(e.target.checked)}
                    />
                    <div className="flex items-center gap-2">
                        <Mail size={18} />
                        <span className="label-text font-bold">
                            Send email notification with login credentials
                        </span>
                    </div>
                </label>
            </div>

            {/* Form Content */}
            {activeTab === "single" ? (
                <div>
                    <FighterAccountForm
                        formData={formData}
                        setFormData={setFormData}
                        errors={formErrors}
                    />
                    
                    {/* Password Preview */}
                    <div className="border-2 border-base-content p-4 mt-4">
                        <label className="label">
                            <span className="label-text font-bold">
                                Generated Password Preview
                            </span>
                        </label>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                readOnly
                                value={passwordPreview}
                                className="input input-bordered flex-1 font-mono"
                                placeholder="Password will be generated after entering name and phone"
                            />
                            {passwordPreview && (
                                <button
                                    className="btn btn-outline"
                                    onClick={() => {
                                        navigator.clipboard.writeText(passwordPreview);
                                        toast.success("Password copied to clipboard");
                                    }}
                                >
                                    Copy
                                </button>
                            )}
                        </div>
                        <label className="label">
                            <span className="label-text-alt text-base-content/70">
                                Pattern: FirstName + Last4DigitsOfPhone + "!"
                            </span>
                        </label>
                    </div>

                    <div className="mt-6 flex justify-end">
                        <button
                            className="btn btn-primary btn-lg w-full"
                            onClick={handleSingleSubmit}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="loading loading-spinner"></span>
                                    Creating...
                                </>
                            ) : (
                                <>
                                    <UserPlus size={20} className="mr-2" />
                                    CREATE ACCOUNT
                                </>
                            )}
                        </button>
                    </div>
                </div>
            ) : (
                <div>
                    <BulkUploadFighter
                        onParseSuccess={handleBulkParseSuccess}
                        onImagesChange={handleBulkImagesChange}
                        errors={[]}
                    />
                    <div className="mt-6 flex justify-end">
                        {bulkFighters.length > 0 && (
                            <div className="flex-1 mr-4 flex items-center">
                                <span className="text-sm">
                                    {Object.keys(bulkUploadedImages).length}/{bulkFighters.length} images uploaded
                                </span>
                            </div>
                        )}
                        <button
                            className="btn btn-primary btn-lg"
                            onClick={handleBulkSubmit}
                            disabled={
                                loading ||
                                bulkFighters.length === 0 ||
                                !bulkFighters.every((_, index) => bulkUploadedImages[index])
                            }
                        >
                            {loading ? (
                                <>
                                    <span className="loading loading-spinner"></span>
                                    Creating...
                                </>
                            ) : (
                                <>
                                    <Upload size={20} className="mr-2" />
                                    CREATE {bulkFighters.length} ACCOUNT(S)
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* Results Table */}
            {results.length > 0 && <AccountResultsTable results={results} />}
        </div>
    );
}
